import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  completeAvatar,
  fetchMe,
  presignAvatar,
  updateProfile,
  uploadAvatarToS3,
  type ProfileUpdateInput,
  type UserMe,
} from "./api";
import { useAuthStore } from "@/lib/auth-store";

const DNI_RE = /^[XYZ]?\d{5,8}[A-Z]$/i;
const PHONE_RE = /^[+0-9 .()-]{6,}$/;

type FormState = {
  name: string;
  surname: string;
  dni: string;
  address: string;
  phone: string;
};

function toForm(me: UserMe | undefined | null): FormState {
  return {
    name: me?.name ?? "",
    surname: me?.surname ?? "",
    dni: me?.dni ?? "",
    address: me?.address ?? "",
    phone: me?.phone ?? "",
  };
}

export function ProfilePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const setUserPartial = useAuthStore((s) => s.user);
  const meQuery = useQuery({ queryKey: ["me"], queryFn: fetchMe });
  const me = meQuery.data;

  const [form, setForm] = useState<FormState>(toForm(null));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>(
    {},
  );
  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const initial = useMemo(() => toForm(me), [me]);

  useEffect(() => {
    if (me) setForm(toForm(me));
  }, [me]);

  const dirty = useMemo(() => {
    return (Object.keys(form) as (keyof FormState)[]).some(
      (k) => form[k] !== initial[k],
    );
  }, [form, initial]);

  // Navigation guard for unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  function validate(): boolean {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) e.name = t("profile.err.name_required");
    if (form.dni.trim() && !DNI_RE.test(form.dni.trim()))
      e.dni = t("profile.err.dni_invalid");
    if (form.phone.trim() && !PHONE_RE.test(form.phone.trim()))
      e.phone = t("profile.err.phone_invalid");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const updateMut = useMutation({
    mutationFn: (payload: ProfileUpdateInput) => updateProfile(payload),
    onSuccess: (data) => {
      qc.setQueryData(["me"], data);
      // Sync auth-store user (name/email used in topbar)
      try {
        const auth = useAuthStore.getState();
        if (auth.user) {
          useAuthStore.setState({
            user: {
              ...auth.user,
              name: data.name,
              email: data.email,
            },
          });
        }
      } catch {
        /* ignore */
      }
      setSavedFlash(t("profile.saved"));
      setTimeout(() => setSavedFlash(null), 3000);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    updateMut.mutate({
      name: form.name.trim(),
      surname: form.surname.trim() || null,
      dni: form.dni.trim() || null,
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
    });
  }

  // Status text
  const statusText = updateMut.isPending
    ? t("profile.status.saving")
    : dirty
      ? t("profile.status.dirty")
      : savedFlash || t("profile.status.clean");

  const userInitial = (me?.name?.[0] || me?.email?.[0] || "U").toUpperCase();
  const userInitials =
    me?.name && me.name.length > 0
      ? me.name[0].toUpperCase() + (me.surname?.[0]?.toUpperCase() || "")
      : userInitial;

  return (
    <div className="profile-root" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <h1 className="page-title">{t("profile.title")}</h1>
      <p className="page-subtitle">{t("profile.subtitle")}</p>

      <AvatarCard me={me} userInitials={userInitials} />

      {me?.location_policy && me.location_policy !== "OFF" && (
        <div className="card profile-card">
          <div className="profile-card-title">{t("profile.location.title")}</div>
          <p style={{ margin: 0, fontSize: "0.9rem" }}>
            {me.location_policy === "REQUIRED" ? (
              <span className="profile-location-badge profile-location-badge--required">
                {t("profile.location.required")}
              </span>
            ) : (
              <span className="profile-location-badge profile-location-badge--optional">
                {t("profile.location.optional")}
              </span>
            )}
          </p>
        </div>
      )}

      <form onSubmit={onSubmit} className="profile-form" noValidate>
        <div className="profile-grid">
          <div className="card profile-card">
            <div className="profile-card-title">{t("profile.account")}</div>
            <div className="form-field">
              <label className="form-label" htmlFor="email">
                {t("profile.email")}
              </label>
              <input
                className="form-input"
                id="email"
                type="email"
                value={me?.email || ""}
                readOnly
              />
              <p className="text-muted profile-help">
                {t("profile.email_hint")}
              </p>
            </div>
          </div>

          <div className="card profile-card">
            <div className="profile-card-title">{t("profile.personal")}</div>
            <Field
              id="name"
              label={t("profile.name")}
              required
              value={form.name}
              error={errors.name}
              onChange={(v) => setForm({ ...form, name: v })}
              autoComplete="name"
            />
            <Field
              id="surname"
              label={t("profile.surname")}
              value={form.surname}
              error={errors.surname}
              onChange={(v) => setForm({ ...form, surname: v })}
              autoComplete="family-name"
            />
            <Field
              id="dni"
              label={t("profile.dni")}
              value={form.dni}
              error={errors.dni}
              onChange={(v) => setForm({ ...form, dni: v })}
              placeholder="00000000X"
              autoComplete="off"
            />
          </div>

          <div className="card profile-card">
            <div className="profile-card-title">{t("profile.contact")}</div>
            <Field
              id="phone"
              label={t("profile.phone")}
              value={form.phone}
              error={errors.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
              placeholder="+34 600 123 456"
              autoComplete="tel"
            />
            <div className="form-field">
              <label className="form-label" htmlFor="address">
                {t("profile.address")}
              </label>
              <textarea
                className="form-textarea"
                id="address"
                rows={3}
                value={form.address}
                onChange={(e) =>
                  setForm({ ...form, address: e.target.value })
                }
                autoComplete="street-address"
              />
            </div>
          </div>
        </div>

        <div className="profile-actions">
          <div className="profile-status" id="profileStatus">
            {statusText}
          </div>
          <div className="profile-buttons">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!dirty || updateMut.isPending}
            >
              {t("profile.save")}
            </button>
            <Link className="btn btn-secondary" to="/shifts/my">
              {t("profile.back")}
            </Link>
          </div>
        </div>

        {updateMut.isError && (
          <div className="field-error" style={{ marginTop: 8 }}>
            {(updateMut.error as Error)?.message || t("profile.err.generic")}
          </div>
        )}
      </form>

      {/* keep reference to avoid linter "unused" if we later need user */}
      {setUserPartial == null && null}
    </div>
  );
}

function Field({
  id,
  label,
  value,
  error,
  onChange,
  required,
  placeholder,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  error?: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div className="form-field">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      <input
        className="form-input"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function AvatarCard({
  me,
  userInitials,
}: {
  me: UserMe | undefined;
  userInitials: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const presign = await presignAvatar(file);
      await uploadAvatarToS3(presign, file);
      return await completeAvatar(presign.object_key);
    },
    onSuccess: (data) => {
      setSuccess(t("profile.avatar.success"));
      setError(null);
      qc.invalidateQueries({ queryKey: ["me"] });
      // Sync topbar avatar
      try {
        const auth = useAuthStore.getState();
        if (auth.user) {
          useAuthStore.setState({
            user: {
              ...auth.user,
              avatar_thumb_url: data.avatar_thumb_url ?? null,
            },
          });
        }
      } catch {
        /* ignore */
      }
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (e: Error) => {
      setError(e?.message || t("profile.avatar.err.upload"));
      setPreviewUrl(null);
    },
  });

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setError(null);
    setSuccess(null);

    if (!/^image\/(jpe?g|png)$/i.test(file.type)) {
      setError(t("profile.avatar.err.type"));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError(t("profile.avatar.err.size"));
      return;
    }

    // Local preview
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(file);

    upload.mutate(file);
  }

  const displayUrl = previewUrl || me?.avatar_profile_url;

  return (
    <div className="card profile-card" id="avatarSection">
      <div className="profile-card-title">{t("profile.avatar.title")}</div>
      <div className="profile-avatar-section">
        <div className="profile-avatar-preview" id="avatarPreviewWrap">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Avatar"
              className="profile-avatar-circle profile-avatar-circle--img"
            />
          ) : (
            <span className="profile-avatar-circle profile-avatar-circle--initials">
              {userInitials}
            </span>
          )}
        </div>
        <div className="profile-avatar-info">
          <div className="profile-avatar-info-title">
            {t("profile.avatar.your")}
          </div>
          <div className="profile-avatar-info-hint">
            JPG / PNG · {t("profile.avatar.max")}
          </div>
          <div className="profile-avatar-actions">
            <label className="btn btn-secondary btn-sm btn-avatar-upload">
              <span>
                {upload.isPending
                  ? t("profile.avatar.uploading")
                  : t("profile.avatar.change")}
              </span>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                onChange={onPick}
                disabled={upload.isPending}
              />
            </label>
          </div>
          {error && (
            <div className="profile-avatar-error" id="avatarError">
              {error}
            </div>
          )}
          {success && (
            <div className="profile-avatar-success" id="avatarSuccess">
              {success}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
